import type { DropdownEntry } from "~/common/types";
import type { MultilevelSelectItemItem } from "~/common/components/Select/MultilevelSelect";
import type { PriceReferenceOption, PriceReferenceSelection } from "./types";

export const PRICE_REFERENCE_EMPTY_OPTION = "-";

export interface PriceReferenceSelectOption extends Omit<DropdownEntry<string>, "label"> {
  label: string;
  groupCode?: PriceReferenceOption["groupCode"];
}

export function getInitialPriceReference(selection: PriceReferenceSelection | undefined): string {
  return selection?.initialValue.code ?? "";
}

export function getPriceReferenceOptions(
  selection: PriceReferenceSelection,
  includeInitialValue = true,
): PriceReferenceSelectOption[] {
  const values = includeInitialValue ? [selection.initialValue, ...selection.options] : selection.options;
  return values.map((option) => toSelectOption(option));
}

export function toSelectOption(option: PriceReferenceOption): PriceReferenceSelectOption {
  const description = option.description?.trim();
  return {
    value: option.code || PRICE_REFERENCE_EMPTY_OPTION,
    label: description ? `${option.code} - ${description}` : option.code || PRICE_REFERENCE_EMPTY_OPTION,
    ...(option.groupCode ? { groupCode: option.groupCode } : {}),
  };
}

export function getGroupedPriceReferenceOptions(
  selection: PriceReferenceSelection,
  translate: (key: string) => string,
): MultilevelSelectItemItem[] {
  const options = getPriceReferenceOptions(selection);
  const groups = new Map<string, PriceReferenceSelectOption[]>();
  const ungrouped: PriceReferenceSelectOption[] = [];

  options.forEach((option) => {
    if (!option.groupCode) {
      ungrouped.push(option);
      return;
    }
    const group = groups.get(option.groupCode) ?? [];
    group.push(option);
    groups.set(option.groupCode, group);
  });

  const items: MultilevelSelectItemItem[] = ungrouped.map(({ value, label }) => ({ value, label }));
  groups.forEach((group, groupCode) => {
    const groupValue = groupCode === "SECTOR" ? "partner.type.tilda.one.translation" : "partner.type.zf.translation";
    items.push({
      label: translate(groupValue),
      value: groupCode,
      title: true,
      children: group.map(({ value, label }) => ({ value, label, parentValue: groupCode })),
    });
  });
  return items;
}
